import React, { useState } from "react";
import { ORDER } from "@/constants/testIds";

/**
 * EmailLog — pro-shop only.
 * Surfaces order.email_log entries (manual emails + their delivery state).
 */
export default function EmailLog({ order }) {
  const [expanded, setExpanded] = useState(null);
  const log = (order.email_log || []).slice().reverse(); // most recent first

  return (
    <section className="surface-card p-8" data-testid={ORDER.emailLogTab}>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="overline">Mail · This Order</div>
          <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Email History</h2>
          <p className="text-sm text-[#7A7A7A] mt-1">
            Every email sent on this order — direction, delivery state, and contents.
          </p>
        </div>
        <span className="status-pill status-submitted">{log.length} sent</span>
      </div>

      {log.length === 0 ? (
        <div className="mt-8 border border-dashed border-[#E5E3D8] p-8 text-center">
          <div className="overline">Quiet</div>
          <p className="font-serif text-xl mt-2 text-[#1A1A1A]">No emails sent yet.</p>
          <p className="text-sm text-[#7A7A7A] mt-1">Use the composer above to send one.</p>
        </div>
      ) : (
        <ul className="mt-6 divide-y divide-[#E5E3D8] border border-[#E5E3D8]" data-testid={ORDER.emailLogList}>
          {log.map((e, i) => {
            const isOpen = expanded === i;
            const rowKey = e.resend_id || `${e.at}-${e.to}-${i}`;
            return (
              <li key={rowKey} className="bg-white">
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : i)}
                  className="w-full text-left px-6 py-4 flex items-start justify-between gap-4 hover:bg-[#FAF9F2] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-serif text-lg text-[#1A1A1A] truncate">{e.subject}</span>
                      <span className={`status-pill ${e.delivered ? "status-delivered" : "status-cancelled"}`}>
                        {e.delivered ? "Delivered" : "Failed"}
                      </span>
                    </div>
                    <div className="text-xs text-[#7A7A7A] uppercase tracking-wider mt-1">
                      {new Date(e.at).toLocaleString()} · {e.from_role === "member" ? "from member" : "from pro shop"} · to{" "}
                      <span className="font-mono text-[#1A422D] normal-case tracking-normal">{e.to}</span>
                      {e.by_name && <> · sent by {e.by_name}</>}
                    </div>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-[#1A422D] flex-shrink-0 mt-1">
                    {isOpen ? "Hide ↑" : "View ↓"}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 pt-1 bg-[#FAF9F2]">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-[#4A4A4A] mb-3">
                      <div>
                        <span className="label-elegant block">Resend ID</span>
                        <span className="font-mono text-[#1A422D]">{e.resend_id || "—"}</span>
                      </div>
                      <div>
                        <span className="label-elegant block">Status</span>
                        <span className={e.delivered ? "text-[#1A422D]" : "text-[#8a1a1a]"}>
                          {e.delivered
                            ? "Accepted by Resend for delivery"
                            : "Delivery declined (verify your sending domain in Resend to reach this address)"}
                        </span>
                      </div>
                    </div>
                    <div className="border-t border-[#E5E3D8] pt-3">
                      <span className="label-elegant block mb-2">Body</span>
                      <div className="font-serif text-sm leading-relaxed whitespace-pre-wrap text-[#1A1A1A]">
                        {e.body}
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
